/**
 * PulseEffect — decorative pulse/halo built on pure Reanimated.
 *
 * Two built-in presets:
 * - **ripple**: scales outward while fading out, then loops. Multiple
 *   instances can be staggered via the `delay` prop to create concentric
 *   ripples (used for alarm-activated state).
 * - **breathe**: gently oscillates scale and opacity back-and-forth (used
 *   for alarm-armed state).
 *
 * The component renders an absolutely-positioned `Animated.View` sized to
 * its parent, so wrap it (and an `IconButton`) inside a parent `View`
 * with explicit width/height.
 *
 * @example
 * ```tsx
 * <View style={{ width: 32, height: 32 }}>
 *   <PulseEffect variant="ripple" color="#F23D6F" delay={0} />
 *   <PulseEffect variant="ripple" color="#F23D6F" delay={400} />
 *   <IconButton ... />
 * </View>
 * ```
 */
import { memo, useEffect, type FC } from "react";
import { StyleSheet } from "react-native";
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from "react-native-reanimated";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type PulseVariant = "ripple" | "breathe";

export interface PulseEffectProps {
  /** Visual preset. */
  readonly variant: PulseVariant;
  /** Fill colour of the halo. */
  readonly color: string;
  /** Delay before the first cycle starts (ms). Useful for staggered ripples. */
  readonly delay?: number;
  /** Width & height override. Falls back to 100% via absoluteFill. */
  readonly size?: number;
}

// ---------------------------------------------------------------------------
// Preset configurations (not user-facing — keeps the public API minimal)
// ---------------------------------------------------------------------------

interface PulsePreset {
  readonly fromOpacity: number;
  readonly toOpacity: number;
  readonly fromScale: number;
  readonly toScale: number;
  readonly duration: number;
  readonly easing: (t: number) => number;
  readonly reverse: boolean;
}

const PRESETS: Record<PulseVariant, PulsePreset> = {
  ripple: {
    fromOpacity: 0.7,
    toOpacity: 0,
    fromScale: 1,
    toScale: 1.5,
    duration: 1500,
    easing: Easing.out(Easing.ease),
    reverse: false,
  },
  breathe: {
    fromOpacity: 0.3,
    toOpacity: 0.6,
    fromScale: 1,
    toScale: 1.2,
    duration: 2000,
    easing: Easing.inOut(Easing.ease),
    reverse: true,
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PulseEffect: FC<PulseEffectProps> = memo(({ variant, color, delay = 0, size }) => {
  const preset = PRESETS[variant];

  const opacity = useSharedValue(preset.fromOpacity);
  const scale = useSharedValue(preset.fromScale);

  useEffect(() => {
    const toOpacity = withTiming(preset.toOpacity, {
      duration: preset.duration,
      easing: preset.easing,
    });
    const toScale = withTiming(preset.toScale, {
      duration: preset.duration,
      easing: preset.easing,
    });

    const repeatCount = -1;

    opacity.value = preset.fromOpacity;
    scale.value = preset.fromScale;

    opacity.value = withDelay(delay, withRepeat(toOpacity, repeatCount, preset.reverse));
    scale.value = withDelay(delay, withRepeat(toScale, repeatCount, preset.reverse));

    return () => {
      cancelAnimation(opacity);
      cancelAnimation(scale);
    };
  }, [variant, delay, opacity, scale, preset]);

  const animatedStyle = useAnimatedStyle(
    () => ({
      opacity: opacity.value,
      transform: [{ scale: scale.value }],
    }),
    [opacity, scale],
  );

  const sizeStyle = size !== undefined ? { width: size, height: size } : undefined;

  return (
    <Animated.View style={[styles.halo, { backgroundColor: color }, sizeStyle, animatedStyle]} pointerEvents="none" />
  );
});

PulseEffect.displayName = "PulseEffect";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  halo: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 999,
  },
});
